import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message?: string;
}

/**
 * Global error boundary: evita la pantalla en blanco si un render falla en producción.
 */
class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[AppErrorBoundary]", error, info.componentStack);
  }

  private handleReload = () => {
    this.setState({ hasError: false, message: undefined });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="min-h-screen flex items-center justify-center bg-background px-6">
        <section className="max-w-md w-full text-center space-y-5 rounded-xl border border-border bg-card p-8 shadow-lg">
          <AlertTriangle className="mx-auto h-10 w-10 text-destructive" aria-hidden="true" />
          <h1 className="text-2xl font-bold text-foreground">Algo salió mal</h1>
          <p className="text-sm text-muted-foreground">
            Ocurrió un error inesperado al mostrar esta sección. Puedes recargar la página o volver al inicio.
          </p>
          {this.state.message && (
            <p className="text-xs text-muted-foreground/70 break-words">{this.state.message}</p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={this.handleReload}>Recargar</Button>
            <Button variant="outline" onClick={() => (window.location.href = "/")}>
              Ir al inicio
            </Button>
          </div>
        </section>
      </main>
    );
  }
}

export default AppErrorBoundary;
